import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const MaxUnlocked = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        date: dateRange
      };
      
      const response = await apiService.post('maxUnlockedList', params);
      if (response && response.data) {
        setVendors(response.data);
        setTotalRecords(response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load max unlocked list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, dateRange]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchVendors();
    }, keyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchVendors]);

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Max Unlocked</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => { setKeyword(e.target.value); setCurrentPage(1); }}
                placeholder="Type Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <input
              type="text"
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }}
              placeholder="Pick date range"
              className="w-full sm:w-full sm:w-[200px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#009ef7'}}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-award text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Vendors Found</h3>
              <p className="text-gray-500">No vendors match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-4 py-3">#</th>
                    <th className="px-2 sm:px-4 py-3">Name</th>
                    <th className="px-2 sm:px-4 py-3">Email</th>
                    <th className="px-2 sm:px-4 py-3 text-center">Unlock Count</th>
                    <th className="px-2 sm:px-4 py-3">Business</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vendors.map((vendor, index) => (
                    <tr key={vendor.userId || index} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                      <td className="px-2 sm:px-4 py-3">
                        <Link to={`/view-profile/${vendor.userId}`} style={{color: '#009ef7'}} className="no-underline hover:opacity-80">
                          {vendor.name ? vendor.name.charAt(0).toUpperCase() + vendor.name.slice(1) : ''}
                        </Link>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="flex flex-col text-xs">
                          <span className="text-gray-700">{vendor.email || ''}</span>
                          <span className="text-gray-500">{vendor.phone || ''}</span>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center font-semibold">{vendor.totalQueries}</td>
                      <td className="px-2 sm:px-4 py-3 text-gray-700">{vendor.businessName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && vendors.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-xs sm:text-sm text-gray-700">Show</span>
              <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1} 
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                «
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  const isActive = currentPage === page;
                  return (
                    <button 
                      key={page} 
                      onClick={() => setCurrentPage(page)} 
                      className="px-3 py-1 border rounded text-xs sm:text-sm"
                      style={{
                        backgroundColor: isActive ? '#009ef7' : '#fff',
                        borderColor: isActive ? '#009ef7' : '#d1d5db',
                        color: isActive ? '#fff' : '#374151'
                      }}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button 
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages} 
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaxUnlocked;
